import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";

const headerCellStyle = {
  fontWeight: 700,
  color: "#fff",
  backgroundColor: "#142C5C",
  fontSize: { xs: "12px", sm: "13px", md: "14px" },
  whiteSpace: "nowrap",
};

const SummaryCard = ({ label, value, hint }) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 160px",
      minWidth: 140,
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      padding: "14px 16px",
    }}
  >
    <Typography
      sx={{ fontSize: "12px", color: "text.secondary", fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#142C5C", mt: 0.5 }}>
      {value}
    </Typography>
    {hint ? (
      <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0.5 }}>
        {hint}
      </Typography>
    ) : null}
  </Paper>
);

/**
 * Reports → Theme Engagement: one row per weekly theme (name + banner +
 * users who opened voting prefs / saw the theme), split into first theme
 * that America/New_York ISO week vs already-had-another that week.
 */
export default function ThemeEngagementPanel({ stats }) {
  const {
    themes = [],
    totals = null,
    loading = false,
    unavailable = false,
  } = stats || {};

  if (unavailable) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: "1px dashed #c9c9c9",
          borderRadius: "8px",
          padding: "20px",
          color: "text.secondary",
          fontSize: "13px",
        }}
      >
        Theme engagement is not available yet — the backend serving this panel
        does not expose /analytics/admin/theme-engagement.
      </Paper>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: "12px",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "16px", md: "18px" },
            color: "#142C5C",
          }}
        >
          Weekly themes
        </Typography>
        <Tooltip
          title="Each row is a weekly theme in the selected date range (or created during it). Seen = accounts that opened Voting Preferences while that theme was active (gear notice marked read). First that week = this was their first theme acknowledgment in that America/New_York Mon–Sun week. Also saw another = they had already seen a different theme earlier the same week."
          placement="right"
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 17, color: "text.disabled", cursor: "help" }}
          />
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          marginBottom: "16px",
        }}
      >
        <SummaryCard
          label="Themes"
          value={loading ? "…" : (totals?.themes ?? themes.length).toLocaleString()}
          hint="Rows in this table"
        />
        <SummaryCard
          label="Total seen"
          value={loading ? "…" : (totals?.seenCount ?? 0).toLocaleString()}
          hint="Sum of per-theme seen counts"
        />
        <SummaryCard
          label="First that week"
          value={
            loading ? "…" : (totals?.firstInWeekCount ?? 0).toLocaleString()
          }
          hint="Acks that were the user's first theme that week"
        />
        <SummaryCard
          label="Also saw another"
          value={
            loading ? "…" : (totals?.multiInWeekCount ?? 0).toLocaleString()
          }
          hint="Acks where the user had already seen another theme that week"
        />
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          maxHeight: "70vh",
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle}>Theme</TableCell>
              <TableCell sx={headerCellStyle}>Window</TableCell>
              <TableCell sx={headerCellStyle} align="right">
                Seen
              </TableCell>
              <TableCell sx={headerCellStyle} align="right">
                First that week
              </TableCell>
              <TableCell sx={headerCellStyle} align="right">
                Also saw another
              </TableCell>
              <TableCell sx={headerCellStyle} align="right">
                Owners
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: "text.secondary", py: 3 }}>
                  Loading theme engagement…
                </TableCell>
              </TableRow>
            ) : themes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: "text.secondary", py: 3 }}>
                  No weekly themes in this date range. Create a theme in Manage
                  Theme — it will show up here with zero counts until users open
                  Voting Preferences.
                </TableCell>
              </TableRow>
            ) : (
              themes.map((row) => (
                <TableRow key={row.themeId} hover>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 36,
                          borderRadius: "6px",
                          overflow: "hidden",
                          flexShrink: 0,
                          backgroundColor: "#eceff3",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {row.bannerImage ? (
                          <Box
                            component="img"
                            src={row.bannerImage}
                            alt=""
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        ) : null}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "13px", md: "14px" },
                          color: "#142C5C",
                        }}
                        noWrap
                      >
                        {row.title || "Untitled theme"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "12px", md: "13px" },
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                    }}
                  >
                    {row.startDate
                      ? dayjs(row.startDate).format("MMM D, YYYY")
                      : "—"}
                    {" – "}
                    {row.endDate
                      ? dayjs(row.endDate).format("MMM D, YYYY")
                      : "—"}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: { xs: "13px", md: "14px" } }}
                  >
                    {(row.seenCount ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontSize: { xs: "13px", md: "14px" } }}
                  >
                    {(row.firstInWeekCount ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontSize: { xs: "13px", md: "14px" } }}
                  >
                    {(row.multiInWeekCount ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontSize: { xs: "13px", md: "14px" } }}
                  >
                    {row.ownerCount == null
                      ? "—"
                      : row.ownerCount.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
